// Lazy-load helper for routes.
//
// Wrapping a route's component with `lazy(() => import("..."))` tells
// Vite to emit a separate chunk for that module; the router fetches
// it the first time the user navigates to a matching path and caches
// the resolved component in memory thereafter.

import type { Component } from "svelte";

// Route components declare their own prop shapes. The Router always
// passes `{ params, children? }`; components without those props
// simply ignore the extra fields (Svelte's runtime is permissive).
// We type the loader as `Component<any>` to avoid forcing every
// view to declare a never-used `params` prop in its `Props`.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRouteComponent = Component<any>;

export interface LazyComponent {
  /** Discriminator so the Router can tell lazy from eager at runtime. */
  __lazy: true;
  /** Resolves to the route component (default export of the imported module). */
  load: () => Promise<AnyRouteComponent>;
}

/**
 * Wrap a dynamic `import()` so the router can split + load on demand.
 *
 * Usage:
 *   { path: "/agents", component: lazy(() => import("./views/Agents.svelte")) }
 *
 * Vite generates `assets/Agents-<hash>.js`, served lazily.
 */
export function lazy(
  loader: () => Promise<{ default: AnyRouteComponent }>,
): LazyComponent {
  return {
    __lazy: true,
    load: async () => (await loader()).default,
  };
}

export function isLazy(x: unknown): x is LazyComponent {
  return (
    typeof x === "object" &&
    x !== null &&
    (x as { __lazy?: unknown }).__lazy === true
  );
}
